import { useEffect, useState } from 'react';
import {
  Activity,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
  Eye,
  Loader2,
  RefreshCw,
  Search,
  X,
} from 'lucide-react';
import { MODULES, type Module } from '@mygmao/shared';
import { api, extractErrorMessage } from '../lib/api';
import { ModalPortal } from '../components/ModalPortal';

/**
 * Page /admin/audit-logs — US-ADM-04.
 *
 * Liste paginée des entrées d'audit du tenant, filtrable par module,
 * action, plage de dates et recherche libre dans le payload. Lecture
 * seule. Le détail (avec `details` JSONB before/after) est affiché dans
 * un drawer.
 */

interface AuditLogRow {
  id: string;
  tenant_id: string | null;
  user_id: string | null;
  action: string;
  module: string | null;
  resource_type: string | null;
  resource_id: string | null;
  ip: string | null;
  user_agent: string | null;
  timestamp: string;
}

interface AuditLogDetail extends AuditLogRow {
  details: unknown;
}

interface Pagination {
  page: number;
  page_size: number;
  total: number;
  total_pages: number;
}

export function AdminAuditLogsPage() {
  const [items, setItems] = useState<AuditLogRow[]>([]);
  const [pagination, setPagination] = useState<Pagination | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [module, setModule] = useState<Module | ''>('');
  const [action, setAction] = useState('');
  const [resourceType, setResourceType] = useState('');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [page, setPage] = useState(1);
  const [detailId, setDetailId] = useState<string | null>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [module, action, resourceType, debouncedSearch, from, to]);

  useEffect(() => {
    refresh();
  }, [module, action, resourceType, debouncedSearch, from, to, page]);

  async function refresh() {
    setLoading(true);
    setErrorMessage(null);
    try {
      const params = new URLSearchParams();
      if (module) params.set('module', module);
      if (action) params.set('action', action);
      if (resourceType) params.set('resource_type', resourceType);
      if (debouncedSearch) params.set('q', debouncedSearch);
      if (from) params.set('from', new Date(from).toISOString());
      if (to) params.set('to', new Date(to).toISOString());
      params.set('page', String(page));
      params.set('page_size', '25');
      const res = await api.get<{ data: AuditLogRow[]; pagination: Pagination }>(
        `/admin/audit-logs?${params}`,
      );
      setItems(res.data.data);
      setPagination(res.data.pagination);
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }

  function resetFilters() {
    setModule('');
    setAction('');
    setResourceType('');
    setSearch('');
    setFrom('');
    setTo('');
  }

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <h1 className="text-2xl font-bold text-gray-900 flex items-center">
        <Activity className="w-7 h-7 mr-2 text-primary-600" />
        Journaux d'audit
      </h1>

      {errorMessage && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
          <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{errorMessage}</p>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-gray-200">
        <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-3 border-b border-gray-200">
          <div className="relative col-span-1 sm:col-span-2 lg:col-span-2">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="search"
              placeholder="Rechercher dans le payload…"
              className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select
            value={module}
            onChange={(e) => setModule(e.target.value as Module | '')}
            className={selectClass}
          >
            <option value="">Tous modules</option>
            {MODULES.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <input
            type="text"
            placeholder="Action (ex. create)"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            className={selectClass}
          />
          <input
            type="text"
            placeholder="Resource (ex. User)"
            value={resourceType}
            onChange={(e) => setResourceType(e.target.value)}
            className={selectClass}
          />
          <button
            onClick={resetFilters}
            className="inline-flex items-center justify-center px-3 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
            title="Réinitialiser les filtres"
          >
            <RefreshCw className="w-4 h-4 mr-1.5" />
            Réinitialiser
          </button>
          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs text-gray-500 block mb-1">Depuis</label>
            <input
              type="datetime-local"
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              className={selectClass}
            />
          </div>
          <div className="col-span-1 sm:col-span-2 lg:col-span-3">
            <label className="text-xs text-gray-500 block mb-1">Jusqu'à</label>
            <input
              type="datetime-local"
              value={to}
              onChange={(e) => setTo(e.target.value)}
              className={selectClass}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Date
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Module
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Action
                </th>
                <th className="hidden md:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Resource
                </th>
                <th className="hidden lg:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  Acteur
                </th>
                <th className="hidden xl:table-cell px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  IP
                </th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  Détail
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center">
                    <Loader2 className="w-6 h-6 animate-spin text-primary-500 mx-auto" />
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                    Aucune entrée d'audit ne correspond aux filtres.
                  </td>
                </tr>
              ) : (
                items.map((log) => (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-xs text-gray-700 whitespace-nowrap">
                      {new Date(log.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      {log.module ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-primary-100 text-primary-800">
                          {log.module}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="font-mono text-xs">{log.action}</span>
                    </td>
                    <td className="hidden md:table-cell px-4 py-3 text-xs text-gray-700">
                      {log.resource_type ?? '—'}
                      {log.resource_id && (
                        <span className="text-gray-400 font-mono">
                          {' '}
                          · {log.resource_id.slice(0, 8)}
                        </span>
                      )}
                    </td>
                    <td className="hidden lg:table-cell px-4 py-3 text-xs text-gray-500 font-mono">
                      {log.user_id ? log.user_id.slice(0, 8) : 'système'}
                    </td>
                    <td className="hidden xl:table-cell px-4 py-3 text-xs text-gray-500">
                      {log.ip ?? '—'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => setDetailId(log.id)}
                        className="inline-flex items-center p-2 rounded-lg text-gray-600 hover:bg-gray-100"
                        title="Voir détail"
                        aria-label="Voir détail"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {pagination && pagination.total_pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 text-sm">
            <span className="text-gray-600">
              {pagination.total} entrée(s) — page {pagination.page} / {pagination.total_pages}
            </span>
            <div className="flex items-center space-x-2">
              <button
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                <ChevronLeft className="w-4 h-4 mr-1" /> Précédent
              </button>
              <button
                disabled={page >= pagination.total_pages}
                onClick={() => setPage((p) => Math.min(pagination.total_pages, p + 1))}
                className="inline-flex items-center px-3 py-1.5 border border-gray-300 rounded-lg text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
              >
                Suivant <ChevronRight className="w-4 h-4 ml-1" />
              </button>
            </div>
          </div>
        )}
      </div>

      {detailId && <AuditLogDetailDrawer id={detailId} onClose={() => setDetailId(null)} />}
    </div>
  );
}

function AuditLogDetailDrawer({ id, onClose }: { id: string; onClose: () => void }) {
  const [log, setLog] = useState<AuditLogDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get<{ data: AuditLogDetail }>(`/admin/audit-logs/${id}`);
        if (!cancelled) setLog(res.data.data);
      } catch (err) {
        if (!cancelled) setErrorMessage(extractErrorMessage(err));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id]);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-30 z-40" onClick={onClose} aria-hidden />
      <aside className="fixed top-0 right-0 z-50 h-screen w-full max-w-2xl bg-white shadow-2xl overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 sticky top-0 bg-white">
          <h2 className="text-lg font-semibold text-gray-900">Détail audit log</h2>
          <button
            onClick={onClose}
            className="p-1 rounded-lg hover:bg-gray-100"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {errorMessage && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-700">{errorMessage}</p>
            </div>
          )}
          {loading || !log ? (
            <div className="flex items-center justify-center p-12">
              <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
            </div>
          ) : (
            <>
              <dl className="text-sm space-y-1.5">
                <DetailRow label="Date" value={new Date(log.timestamp).toLocaleString()} />
                <DetailRow
                  label="Action"
                  value={<code className="font-mono text-xs">{log.action}</code>}
                />
                <DetailRow label="Module" value={log.module ?? '—'} />
                <DetailRow
                  label="Resource"
                  value={
                    log.resource_type
                      ? `${log.resource_type}${log.resource_id ? ` · ${log.resource_id}` : ''}`
                      : '—'
                  }
                />
                <DetailRow
                  label="Acteur"
                  value={
                    log.user_id ? (
                      <code className="font-mono text-xs">{log.user_id}</code>
                    ) : (
                      <em className="text-gray-500">système</em>
                    )
                  }
                />
                <DetailRow label="IP" value={log.ip ?? '—'} />
                <DetailRow label="User-Agent" value={log.user_agent ?? '—'} />
                <DetailRow
                  label="Tenant"
                  value={log.tenant_id ?? <em className="text-gray-500">plateforme</em>}
                />
              </dl>

              <section>
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  Détails
                </h3>
                <pre className="bg-gray-900 text-gray-100 text-xs font-mono p-3 rounded-lg overflow-x-auto whitespace-pre-wrap">
                  {log.details === null || log.details === undefined
                    ? 'null'
                    : JSON.stringify(log.details, null, 2)}
                </pre>
              </section>
            </>
          )}
        </div>
      </aside>
    </ModalPortal>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-2 py-1 border-b border-gray-100 last:border-0">
      <dt className="text-xs text-gray-500">{label}</dt>
      <dd className="text-gray-900 text-right break-all max-w-[60%]">{value}</dd>
    </div>
  );
}

const selectClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';
